import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import StaffSidebarLayout from './StaffSidebarLayout';
import '../shops/shopStyles.css';

function PackageSubscriptions() {
  const navigate = useNavigate();
  const location = useLocation();
  const [availablePackages, setAvailablePackages] = useState([]);
  const [subscribedPackages, setSubscribedPackages] = useState([]);
  const [activeTab, setActiveTab] = useState('available'); // 'available' or 'subscribed'
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [processing, setProcessing] = useState(null); // Package ID being processed
  const [showSubscribeConfirm, setShowSubscribeConfirm] = useState(false);
  // Acceptance of the professional package terms. The server REQUIRES it (409
  // agreement_not_accepted), so this is the client half of the same gate.
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  // The professional's own services that a missing package line could be LINKED to (unlinked, or
  // pointing at a template that no longer exists). Sent by the listing so the fix screen does not
  // need a second fetch.
  const [linkableServices, setLinkableServices] = useState([]);

  // The fix path (Phase 15D part 2). Populated from a 409 services_not_offered, or from a card that
  // already knows services are missing. It holds the missing template lines and the professional's
  // per-line choice: link one of their own services, or create a copy from the shop's template.
  const [fixState, setFixState] = useState(null);

  // An OWNER-OPERATOR sets the package terms rather than accepting them, so the modal must not ask
  // them to agree to a document they author. It is also correct for money: the owner rule pays the
  // BUSINESS, not the professional, so a "you will be paid $X per session" term is one they cannot
  // benefit from. They can still subscribe and deliver sessions — that is the point of the
  // exemption — and the server applies the same rule.
  //
  // Declared AFTER `selectedPackage`, which it reads. Declaring it above threw
  // "can't access lexical declaration 'selectedPackage' before initialization" — a temporal dead
  // zone error at render, which crashes the whole page rather than just the modal.
  const viewerIsOwnerOperator =
    selectedPackage?.splitMethod === 'owner_as_professional';

  // Get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('ogestyle_token');
    if (!token) {
      navigate('/login');
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  };

  // Get API base URL
  const getApiBaseUrl = () => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL || !baseURL.startsWith('http')) {
      setErrorMsg('Invalid API configuration. Please contact support.');
      return null;
    }
    return baseURL;
  };

  // All professionals use unified professional role type
  const getRoleType = useCallback(() => {
    return 'professional';
  }, []);

  // Fetch packages
  const fetchPackages = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      // Get staff profile first to get shopId and staffId
      const profileRes = await axios.get(`${baseURL}/api/staff/profile`, { headers });
      // The API returns { success: true, staff: {...} }
      const staffProfile = profileRes.data.staff || profileRes.data;
      
      const staffId = staffProfile._id;
      
      // PACKAGES ARE BUSINESS-LEVEL: All professionals see all business packages
      const business = staffProfile.businessId;
      const businessId = typeof business === 'object' ? business._id : business;

      if (!businessId) {
        setErrorMsg('No business found. Please contact your administrator.');
        setLoading(false);
        return;
      }
      // Fetch available packages and subscribed packages
      const [availableRes, subscribedRes] = await Promise.all([
        axios.get(`${baseURL}/api/packages/available/${businessId}`, { 
          headers,
          params: { staffId }
        }),
        axios.get(`${baseURL}/api/packages/my-subscriptions/${staffId}`, { headers })
      ]);

      const subscribedPkgs = subscribedRes.data.packages || [];
      const subscribedIds = new Set(subscribedPkgs.map(p => p._id));
      
      // Filter out already subscribed packages from available list
      const availablePkgs = (availableRes.data.packages || []).filter(pkg => !subscribedIds.has(pkg._id));

      setAvailablePackages(availablePkgs);
      setSubscribedPackages(subscribedPkgs);
      // Candidates for the "Use one of my services" picker, computed server-side so the client does
      // not have to know the family-id rule.
      setLinkableServices(availableRes.data.linkableServices || []);
      setErrorMsg('');
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || 'Unable to load packages.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  // Open the professional terms modal for a package. Kept separate from `handleSubscribe` so the fix
  // path can reach it AFTER the services are resolved, without re-checking (stale) missing services.
  const openTermsModal = (pkg) => {
    setSelectedPackage(pkg);
    // Reset per package: acceptance is a decision about THIS subscription, not a sticky toggle
    // that a professional ticks once and carries into every later package.
    setAgreementAccepted(false);
    setShowSubscribeConfirm(true);
  };

  // Show subscribe confirmation — but when the card ALREADY knows a service is missing, the FIX runs
  // first. That ordering is the point: a professional who cannot deliver the package must not accept
  // the terms (and the server must not record them) for a subscription that will not happen. The
  // terms belong to the subscription that follows the fix.
  const handleSubscribe = (pkg) => {
    if (pkg?.missingServices && pkg.missingServices.length > 0) {
      openFixPath(pkg, pkg.missingServices, linkableServices, {
        retryAfterFix: false,
      });
      return;
    }
    openTermsModal(pkg);
  };

  // Build the per-line default: LINK when the professional already has a same-named candidate,
  // otherwise leave the picker empty so they must choose one or switch to create.
  const buildFixChoices = useCallback((missing, candidates) => {
    const choices = {};
    for (const line of missing) {
      // Only an UNLINKED (or dangling) candidate can be preselected: an already-linked service is
      // disabled in the picker, so preselecting it would leave the select invalid.
      const match = (candidates || []).find(
        (candidate) =>
          !candidate?.linkedToLiveTemplate &&
          candidate?.name &&
          line?.name &&
          candidate.name.trim().toLowerCase() === line.name.trim().toLowerCase(),
      );
      choices[line.templateId] = {
        mode: 'link',
        serviceId: match ? match._id : '',
      };
    }
    return choices;
  }, []);

  const openFixPath = useCallback(
    (pkg, missing, candidates, { retryAfterFix = false } = {}) => {
      const services =
        (candidates && candidates.length > 0 ? candidates : linkableServices) ||
        [];
      setFixState({
        // The package itself is kept so the fix can continue to the TERMS (pre-emptive path) or
        // straight to the subscription (retry path) without depending on `selectedPackage`.
        package: pkg,
        packageId: pkg._id,
        packageName: pkg.packageName || pkg.name || 'this package',
        missing: missing.map((line) => ({
          templateId: line.templateId,
          name: line.name,
        })),
        candidates: services,
        choices: buildFixChoices(missing, services),
        retryAfterFix,
        error: '',
        submitting: false,
      });
    },
    [buildFixChoices, linkableServices],
  );

  // The subscribe POST, reusable so the fix path can RETRY it once the services are resolved.
  const subscribeToPackage = async (pkg) => {
    try {
      setProcessing(pkg._id);

      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return false;

      await axios.post(
        `${baseURL}/api/packages/${pkg._id}/subscribe`,
        { agreementAccepted: true },
        { headers },
      );

      setSuccessMsg('Successfully subscribed to package!');
      setTimeout(() => setSuccessMsg(''), 3000);

      // Refresh packages
      await fetchPackages();
      setSelectedPackage(null);
      return true;
    } catch (err) {
      const data = err?.response?.data;

      // THE BLOCK IS NOT A DEAD END ANY MORE. A 409 services_not_offered carries the missing lines
      // and the professional's linkable services, so instead of an error sentence they get the fix
      // screen. The subscription is retried automatically once the fix is applied.
      if (
        err?.response?.status === 409 &&
        data?.error === 'services_not_offered' &&
        Array.isArray(data.missingServiceLines)
      ) {
        setErrorMsg('');
        openFixPath(pkg, data.missingServiceLines, data.candidateServices, {
          retryAfterFix: true,
        });
        return false;
      }

      // The MESSAGE first, the code only as a fallback. Preferring `error` is why the page showed a
      // bare "services_not_offered" — a code is not an explanation, and this one had a real sentence
      // behind it naming the service the professional does not offer. Matches every other handler in
      // this file.
      setErrorMsg(
        data?.message || data?.error || 'Unable to subscribe to package.',
      );
      setTimeout(() => setErrorMsg(''), 5000);
      return false;
    } finally {
      setProcessing(null);
    }
  };

  // Confirm subscription
  const confirmSubscribe = async () => {
    if (!selectedPackage) return;

    // Guarded rather than relying on the disabled button: the server would refuse anyway, but a
    // clear message here beats a 409 arriving after the modal has already closed. An owner-operator
    // is exempt — they set the terms rather than accepting them.
    if (!viewerIsOwnerOperator && !agreementAccepted) {
      setErrorMsg('Please accept the professional package terms to continue.');
      setTimeout(() => setErrorMsg(''), 5000);
      return;
    }

    setShowSubscribeConfirm(false);
    await subscribeToPackage(selectedPackage);
  };

  // Apply the choice made per missing line, then RETRY the subscription that was refused.
  const submitFixPath = async () => {
    if (!fixState) return;

    // A link needs a chosen service. Create needs nothing else — the template supplies everything.
    const unresolved = fixState.missing.filter(
      (line) =>
        fixState.choices[line.templateId]?.mode !== 'create' &&
        !fixState.choices[line.templateId]?.serviceId,
    );

    if (unresolved.length > 0) {
      setFixState((prev) => ({
        ...prev,
        error: `Choose one of your services for ${unresolved
          .map((line) => line.name)
          .join(', ')}, or create a copy from the template.`,
      }));
      return;
    }

    const resolutions = fixState.missing.map((line) => {
      const choice = fixState.choices[line.templateId] || {};
      return choice.mode === 'create'
        ? { templateId: line.templateId }
        : { templateId: line.templateId, serviceId: choice.serviceId };
    });

    const pkg = fixState.package || selectedPackage;
    const retryAfterFix = fixState.retryAfterFix === true;
    setFixState((prev) => ({ ...prev, submitting: true, error: '' }));

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await axios.post(
        `${baseURL}/api/packages/${fixState.packageId}/resolve-missing-services`,
        { resolutions },
        { headers },
      );

      setFixState(null);
      if (pkg) {
        if (retryAfterFix) {
          // The terms were already accepted for the subscription that was refused — retry it.
          setSuccessMsg('Your services were updated. Subscribing…');
          await subscribeToPackage(pkg);
        } else {
          // The fix ran BEFORE the terms. Now that this professional can deliver the package, take
          // them through the terms acceptance and the subscription that follows it.
          setSuccessMsg('Your services were updated.');
          openTermsModal(pkg);
        }
      }
    } catch (err) {
      setFixState((prev) =>
        prev
          ? {
              ...prev,
              submitting: false,
              error:
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                'Unable to update your services.',
            }
          : prev,
      );
    }
  };

  const setFixChoice = (templateId, patch) => {
    setFixState((prev) =>
      prev
        ? {
            ...prev,
            error: '',
            choices: {
              ...prev.choices,
              [templateId]: { ...prev.choices[templateId], ...patch },
            },
          }
        : prev,
    );
  };

  // Unsubscribe from package
  const handleUnsubscribe = async (packageId) => {
    const confirmed = window.confirm(
      'Are you sure you want to unsubscribe from this package? You will still need to honor any existing customer bookings.'
    );
    
    if (!confirmed) return;

    try {
      setProcessing(packageId);
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await axios.post(`${baseURL}/api/packages/${packageId}/unsubscribe`, {}, { headers });
      
      setSuccessMsg('Successfully unsubscribed from package.');
      setTimeout(() => setSuccessMsg(''), 3000);
      
      // Refresh packages
      await fetchPackages();
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || 'Unable to unsubscribe from package.');
      setTimeout(() => setErrorMsg(''), 5000);
    } finally {
      setProcessing(null);
    }
  };

  // Format service names
  const formatServiceNames = (services) => {
    if (!services || services.length === 0) return 'No services';
    return services.map(s => s.serviceName || s.serviceId?.name || s.name || 'Unknown').join(', ');
  };

  if (loading) {
    return (
      <StaffSidebarLayout roleType={getRoleType()}>
        <div className="loading">Loading packages...</div>
      </StaffSidebarLayout>
    );
  }

  return (
    <StaffSidebarLayout roleType={getRoleType()}>
      <div className="staff-profile">
        {/* Back Button */}
        <button
          onClick={() => navigate(`/dashboard/${getRoleType()}`)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'none',
            border: 'none',
            color: '#2d6da3',
            fontSize: '15px',
            fontWeight: '500',
            cursor: 'pointer',
            padding: '8px 0',
            marginBottom: '20px'
          }}
        >
          ← Back to Dashboard
        </button>

        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '28px', color: '#1e293b' }}>
            📦 Package Subscriptions
          </h2>
          <p style={{ color: '#64748b', fontSize: '15px', margin: 0 }}>
            Subscribe to packages to earn from package bookings
          </p>
        </div>

        {/* Success/Error Messages */}
        {successMsg && (
          <div style={{
            padding: '16px',
            marginBottom: '20px',
            background: '#d1fae5',
            color: '#065f46',
            borderRadius: '8px',
            border: '1px solid #a7f3d0'
          }}>
            {successMsg}
          </div>
        )}
        
        {errorMsg && (
          <div style={{
            padding: '16px',
            marginBottom: '20px',
            background: '#fee2e2',
            color: '#991b1b',
            borderRadius: '8px',
            border: '1px solid #fecaca'
          }}>
            {errorMsg}
          </div>
        )}

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '24px',
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '0'
        }}>
          <button
            onClick={() => setActiveTab('available')}
            style={{
              padding: '12px 24px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'available' ? '3px solid #2d6da3' : '3px solid transparent',
              color: activeTab === 'available' ? '#2d6da3' : '#64748b',
              fontWeight: activeTab === 'available' ? '600' : '500',
              fontSize: '15px',
              cursor: 'pointer',
              marginBottom: '-2px'
            }}
          >
            Available Packages ({availablePackages.length})
          </button>
          <button
            onClick={() => setActiveTab('subscribed')}
            style={{
              padding: '12px 24px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'subscribed' ? '3px solid #2d6da3' : '3px solid transparent',
              color: activeTab === 'subscribed' ? '#2d6da3' : '#64748b',
              fontWeight: activeTab === 'subscribed' ? '600' : '500',
              fontSize: '15px',
              cursor: 'pointer',
              marginBottom: '-2px'
            }}
          >
            My Subscriptions ({subscribedPackages.length})
          </button>
        </div>

        {/* Available Packages Tab */}
        {activeTab === 'available' && (
          <div>
            {availablePackages.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '48px 20px',
                background: '#f9fafb',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
                <h3 style={{ color: '#666', fontSize: '17px', marginBottom: '8px' }}>
                  No Available Packages
                </h3>
                <p style={{ color: '#999', fontSize: '15px' }}>
                  Check back later for new package opportunities
                </p>
              </div>
            ) : (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '20px'
              }}>
                {availablePackages.map(pkg => (
                  <div
                    key={pkg._id}
                    style={{
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      padding: '24px',
                      transition: 'all 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                      display: 'flex',
                      flexDirection: 'column',
                      // Needed for the discount badge below, which is positioned absolutely in the
                      // top-right corner exactly as the business's package card does it.
                      position: 'relative'
                    }}
                  >
                    {/* Discount badge — same placement and styling as the business's package card,
                        so a professional and a shop are looking at the same object the same way. */}
                    {pkg.regularPrice > pkg.packagePrice && (
                      <div style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        color: 'white',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '14px',
                        fontWeight: '700'
                      }}>
                        {Math.round(((pkg.regularPrice - pkg.packagePrice) / pkg.regularPrice) * 100)}% OFF
                      </div>
                    )}

                    {/* Package Header */}
                    <div style={{ marginBottom: '16px' }}>
                      <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a', marginBottom: '8px' }}>
                        {pkg.packageName}
                      </h3>
                      {pkg.description && (
                        <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px', lineHeight: '1.5' }}>
                          {pkg.description}
                        </p>
                      )}
                    </div>

                    {/* Price — the same hierarchy as the business card: the package price
                        dominant, the regular price struck through beneath it. */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '32px', fontWeight: '700', color: '#2d6da3' }}>
                        ${pkg.packagePrice?.toFixed(2)}
                      </div>
                      {pkg.regularPrice > pkg.packagePrice && (
                        <div style={{ fontSize: '16px', color: '#999', textDecoration: 'line-through' }}>
                          ${pkg.regularPrice?.toFixed(2)}
                        </div>
                      )}
                    </div>

                    {/* Details — label/value rows, matching the business card's arrangement. */}
                    <div style={{
                      borderTop: '1px solid #e5e7eb',
                      paddingTop: '16px',
                      marginBottom: '16px',
                      fontSize: '14px',
                      color: '#666'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                        <span>🎫 Total Sessions:</span>
                        <span style={{ fontWeight: '600', color: '#1a1a1a' }}>{pkg.totalSessions}</span>
                      </div>
                      {pkg.validityPeriod?.value ? (
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                          <span>⏰ Validity:</span>
                          <span style={{ fontWeight: '600', color: '#1a1a1a' }}>
                            {pkg.validityPeriod.value} {pkg.validityPeriod.unit}
                          </span>
                        </div>
                      ) : null}
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                        <span>👥 Subscribers:</span>
                        <span style={{
                          fontWeight: '600',
                          color: (pkg.subscribedStaffIds?.length || 0) > 0 ? '#10b981' : '#ef4444'
                        }}>
                          {pkg.subscribedStaffIds?.length || 0}
                          {(pkg.subscribedStaffIds?.length || 0) === 0 && (
                            <span style={{
                              fontSize: '11px',
                              marginLeft: '6px',
                              background: '#fee2e2',
                              color: '#dc2626',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontWeight: '600'
                            }}>
                              Be the first
                            </span>
                          )}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                        <span>📦 Services:</span>
                        <span style={{ fontWeight: '600', color: '#1a1a1a', textAlign: 'right' }}>
                          {formatServiceNames(pkg.services)}
                        </span>
                      </div>
                    </div>

                    {/* Earnings Display.
                        Four states, deliberately distinguished. A professional whose commission is
                        not set must NEVER see a bare "$0.00" — that reads as a real rate of zero
                        rather than as a missing one, and it is a figure nobody agreed to.

                        And NO earnings are quoted for a service this professional cannot deliver.
                        Quoting "$X per session" for uncovered work is the same false promise the
                        server refuses on submit (Phase 15D part 2), so the card says what is
                        missing instead of what it would pay. */}
                    {pkg.missingServices && pkg.missingServices.length > 0 ? (
                      <div style={{
                        background: '#fff7ed',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #fdba74',
                        marginBottom: '16px'
                      }}>
                        <div style={{ fontSize: '13px', color: '#9a3412', marginBottom: '6px', fontWeight: '700' }}>
                          ⚠️ You do not offer {pkg.missingServices.length > 1 ? 'these services' : 'this service'}
                        </div>
                        <div style={{ fontSize: '13px', color: '#7c2d12', lineHeight: 1.6 }}>
                          {pkg.missingServices.map((service) => service.name).join(', ')}
                        </div>
                        <div style={{ fontSize: '12px', color: '#9a3412', marginTop: '8px', lineHeight: 1.5 }}>
                          Link one of your own services to it, or add it from the shop's template, and
                          your earnings will appear here. Until then this package cannot be subscribed.
                        </div>
                      </div>
                    ) : pkg.splitMethod === 'owner_as_professional' ? (
                      <div style={{
                        background: '#eff6ff',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #bfdbfe',
                        marginBottom: '16px'
                      }}>
                        <div style={{ fontSize: '13px', color: '#1e40af', marginBottom: '4px', fontWeight: '600' }}>
                          💰 Package Revenue
                        </div>
                        <div style={{ fontSize: '13px', color: '#1e3a8a', lineHeight: '1.5' }}>
                          As the business owner, package revenue is paid to your business rather than to
                          you as a professional.
                        </div>
                      </div>
                    ) : pkg.estimatedEarning === null || pkg.estimatedEarning === undefined ? (
                      <div style={{
                        background: '#fffbeb',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #fcd34d',
                        marginBottom: '16px'
                      }}>
                        <div style={{ fontSize: '13px', color: '#92400e', marginBottom: '4px', fontWeight: '600' }}>
                          💰 Your Average Earnings Per Session
                        </div>
                        <div style={{ fontSize: '13px', color: '#78350f', lineHeight: '1.5' }}>
                          This package cannot quote you a rate. Your commission split appears to be
                          missing from your profile — please ask the business to check it.
                        </div>
                      </div>
                    ) : (
                      <div style={{
                        background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #10b981',
                        marginBottom: '16px'
                      }}>
                        <div style={{ fontSize: '13px', color: '#065f46', marginBottom: '4px', fontWeight: '600' }}>
                          💰 Your Average Earnings Per Session
                        </div>
                        <div style={{ fontSize: '28px', fontWeight: '700', color: '#047857', marginBottom: '4px' }}>
                          ${pkg.estimatedEarning.toFixed(2)}
                        </div>
                        {/* AVERAGE, SAID PLAINLY (user, 2026-10-12: *"fix the wordings … to read average since the
                            service prices are likely to be different"*).

                            This card used to read "Your Earnings Per Session", which was true only while every
                            session paid the same figure — the package's average. A session is now valued by the
                            SERVICE IT DELIVERED, so a $45 service and a $20 service on one package pay
                            differently, and a single unqualified number beside them would be contradicted by the
                            first payout. The figure itself is unchanged: it is the average, and it is what the
                            card must call it. */}
                        <div style={{ fontSize: '12px', color: '#059669' }}>
                          ${(pkg.estimatedEarning * pkg.totalSessions).toFixed(2)} in total for all {pkg.totalSessions} sessions,
                          if they are all the average service
                        </div>

                        {/* The professional's OWN comparison: what the same sessions would pay if
                            booked one by one. Their figures only — no business share, no Stripe
                            detail — and no commentary. The two numbers speak for themselves; the
                            extra sentences only made the card harder to read. */}
                        {pkg.normalBookingPerSession === null ||
                        pkg.normalBookingPerSession === undefined ? null : (
                          <div
                            style={{
                              marginTop: '10px',
                              paddingTop: '10px',
                              borderTop: '1px solid #a7f3d0',
                              fontSize: '12px',
                              color: '#065f46',
                              lineHeight: '1.6'
                            }}
                          >
                            <div>
                              These {pkg.totalSessions} sessions booked one by one would pay you an
                              average of
                            </div>
                            <div style={{ fontWeight: '700', marginTop: '2px' }}>
                              ${pkg.normalBookingPerSession.toFixed(2)} per session (${pkg.normalBookingTotal.toFixed(2)} in total)
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Subscribe Button */}
                    <button
                      onClick={() => handleSubscribe(pkg)}
                      disabled={processing === pkg._id}
                      style={{
                        width: '100%',
                        padding: '14px',
                        background: processing === pkg._id ? '#9ca3af' : 'linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '15px',
                        fontWeight: '600',
                        cursor: processing === pkg._id ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        marginTop: 'auto'
                      }}
                    >
                      {processing === pkg._id ? 'Subscribing...' : '✓ Subscribe to Package'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Subscribed Packages Tab */}
        {activeTab === 'subscribed' && (
          <div>
            {subscribedPackages.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '48px 20px',
                background: '#f9fafb',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
                <h3 style={{ color: '#666', fontSize: '17px', marginBottom: '8px' }}>
                  No Subscriptions Yet
                </h3>
                <p style={{ color: '#999', fontSize: '15px', marginBottom: '20px' }}>
                  Subscribe to available packages to start earning
                </p>
                <button
                  onClick={() => setActiveTab('available')}
                  style={{
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  View Available Packages
                </button>
              </div>
            ) : (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '20px'
              }}>
                {subscribedPackages.map(pkg => (
                  <div
                    key={pkg._id}
                    style={{
                      background: '#fff',
                      border: '2px solid #10b981',
                      borderRadius: '12px',
                      padding: '24px',
                      boxShadow: '0 4px 6px rgba(16, 185, 129, 0.1)',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'start', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <h3 style={{ margin: 0, fontSize: '20px', color: '#1e293b' }}>
                            {pkg.packageName}
                          </h3>
                          <span style={{
                            padding: '4px 12px',
                            background: '#d1fae5',
                            color: '#065f46',
                            fontSize: '12px',
                            fontWeight: '600',
                            borderRadius: '12px'
                          }}>
                            ✓ Active
                          </span>
                        </div>
                        {pkg.description && (
                          <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
                            {pkg.description}
                          </p>
                        )}
                      </div>
                    </div>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '16px',
                      marginBottom: '16px',
                      padding: '16px',
                      background: '#f9fafb',
                      borderRadius: '8px'
                    }}>
                      <div>
                        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>
                          Sessions
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                          {pkg.totalSessions}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>
                          Earnings/Session
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#10b981' }}>
                          {/* Never render a missing rate as "0.00" — that is a figure, not a gap. */}
                          {pkg.estimatedEarning === null || pkg.estimatedEarning === undefined
                            ? 'Not set'
                            : `$${pkg.estimatedEarning.toFixed(2)}`}
                        </div>
                      </div>
                      {pkg.activePurchases !== undefined && (
                        <div>
                          <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>
                            Active Bookings
                          </div>
                          <div style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                            {pkg.activePurchases}
                          </div>
                        </div>
                      )}
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>
                        Services Included:
                      </div>
                      <div style={{ fontSize: '14px', color: '#1e293b' }}>
                        {formatServiceNames(pkg.services)}
                      </div>
                    </div>

                    <button
                      onClick={() => handleUnsubscribe(pkg._id)}
                      disabled={processing === pkg._id}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: processing === pkg._id ? '#9ca3af' : 'white',
                        color: processing === pkg._id ? 'white' : '#dc2626',
                        border: processing === pkg._id ? 'none' : '2px solid #dc2626',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: processing === pkg._id ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        marginTop: 'auto'
                      }}
                    >
                      {processing === pkg._id ? 'Processing...' : 'Unsubscribe'}
                    </button>
                    {pkg.activePurchases > 0 && (
                      <div style={{
                        marginTop: '8px',
                        padding: '8px',
                        background: '#fef3c7',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: '#92400e',
                        textAlign: 'center'
                      }}>
                        ⚠️ You have {pkg.activePurchases} active customer booking(s) with this package
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Mobile Responsive Styles */}
        <style>{`
          @media (max-width: 768px) {
            .staff-profile h2 {
              font-size: 24px !important;
            }
            
            .staff-profile > div:first-of-type p {
              font-size: 14px !important;
            }
          }
          
          @media (max-width: 576px) {
            .staff-profile {
              padding: 16px !important;
            }
            
            .staff-profile h2 {
              font-size: 22px !important;
            }
            
            /* Single column on mobile */
            .staff-profile > div > div[style*="grid"] {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>

        {/* Subscribe Confirmation Modal */}
        {showSubscribeConfirm && selectedPackage && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              maxWidth: '500px',
              width: '100%',
              padding: '32px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '24px', color: '#1e293b' }}>
                  Subscribe to Package
                </h3>
                <p style={{ color: '#64748b', fontSize: '16px', margin: 0 }}>
                  {selectedPackage.packageName}
                </p>
              </div>

              {/* An OWNER-OPERATOR is shown NO terms at all. The list below is a PROFESSIONAL's terms,
                  and for an owner it read as self-contradictory: "You will be paid $0.00 per
                  completed session". That $0.00 is the owner rule working correctly — the business
                  receives the whole amount (D48) — but as a bullet in a terms list it reads like a
                  bug or a pay cut. An owner-operator SETS these terms rather than agreeing to them,
                  so the modal simply lets them subscribe. */}
              {viewerIsOwnerOperator ? null : (
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '24px',
                textAlign: 'left'
              }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#1e293b', fontWeight: '600' }}>
                  Terms & Conditions
                </h4>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#64748b', fontSize: '14px', lineHeight: '1.8' }}>
                  <li>You agree to honor all customer bookings for this package, and to deliver every session sold under it</li>
                  <li>You will provide services at the quality expected by customers</li>
                  <li>You must honor existing bookings even if you unsubscribe later</li>
                  <li>You will be paid <strong>{selectedPackage.estimatedEarning === null || selectedPackage.estimatedEarning === undefined || (selectedPackage.missingServices && selectedPackage.missingServices.length > 0) ? 'your agreed rate' : `an average of $${selectedPackage.estimatedEarning.toFixed(2)}`} per completed session</strong> — one payment per session actually delivered, not at purchase. A session is paid on the <strong>average</strong> only when every service on the package is worth the same; where they differ, a session pays the value of the service actually delivered</li>
                  <li>You are not paid for sessions you do not deliver, and a session you cancel returns to the customer</li>
                  <li>You must deliver a package a customer has <strong>reactivated</strong>, and you may not substitute a lesser service or charge again for a session already covered</li>
                  <li>You will be visible to customers when they book this package</li>
                </ul>
                <p style={{ margin: '12px 0 0 0', fontSize: '12px', color: '#94a3b8', lineHeight: '1.5' }}>
                  The rate shown is what you will be paid, according to your agreement with the business.
                </p>
              </div>
              )}

              {/* Acceptance is recorded server-side against the professional, with the version read
                  from settings — not from this screen. Without it there is no provable agreement. */}
              {viewerIsOwnerOperator ? (
                <div
                  style={{
                    background: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '20px',
                    fontSize: '13px',
                    color: '#1e3a8a',
                    lineHeight: '1.5'
                  }}
                >
                  As the business owner-operator you are not asked to accept the professional terms —
                  you set them. Package revenue is paid to your business.
                </div>
              ) : (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', marginBottom: '20px', fontSize: '13px', color: '#334155' }}>
                  <input
                    type="checkbox"
                    checked={agreementAccepted}
                    onChange={(e) => setAgreementAccepted(e.target.checked)}
                    style={{ width: '16px', height: '16px', marginTop: '2px' }}
                  />
                  <span>
                    I have read and accept the professional package terms, including payment per completed
                    session and the duty to deliver reactivated packages.
                  </span>
                </label>
              )}

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => {
                    setShowSubscribeConfirm(false);
                    setSelectedPackage(null);
                  }}
                  style={{
                    flex: 1,
                    padding: '14px 24px',
                    background: '#f1f5f9',
                    color: '#475569',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSubscribe}
                  disabled={processing || (!viewerIsOwnerOperator && !agreementAccepted)}
                  style={{
                    flex: 1,
                    padding: '14px 24px',
                    background: processing || (!viewerIsOwnerOperator && !agreementAccepted) ? '#9ca3af' : 'linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: processing || (!viewerIsOwnerOperator && !agreementAccepted) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {processing
                    ? 'Subscribing...'
                    : viewerIsOwnerOperator
                      ? 'Subscribe to Package'
                      : 'I Agree, Subscribe'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Fix path modal (Phase 15D part 2).
            Shown when a subscription is refused with 409 services_not_offered, or opened from a card
            that already reports missing services. Each missing line gets two actions: link one of the
            professional's own services (PREFERRED when a same-named candidate exists), or create a
            copy from the shop's template. Saving retries the subscription that was refused. */}
        {fixState && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              maxWidth: '520px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '28px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '18px' }}>
                <div style={{ fontSize: '44px', marginBottom: '12px' }}>🧩</div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '22px', color: '#1e293b' }}>
                  Add the missing services
                </h3>
                <p style={{ color: '#64748b', fontSize: '15px', margin: 0 }}>
                  {fixState.packageName}
                </p>
              </div>

              <p style={{ fontSize: '14px', color: '#475569', lineHeight: 1.6, marginTop: 0 }}>
                This package includes services you do not offer. For each one, link a service you
                already have or create your own copy from the shop's template. Linking never changes
                your own price or duration.
              </p>

              {fixState.error && (
                <div style={{
                  padding: '12px',
                  marginBottom: '16px',
                  background: '#fee2e2',
                  color: '#991b1b',
                  borderRadius: '8px',
                  border: '1px solid #fecaca',
                  fontSize: '14px'
                }}>
                  {fixState.error}
                </div>
              )}

              {fixState.missing.map((line) => {
                const choice = fixState.choices[line.templateId] || {
                  mode: 'link',
                  serviceId: '',
                };
                // Services already bound to a live template are HIDDEN, not shown disabled: linking one
                // would replace that mapping, and listing it — even greyed out — still invites the wrong
                // choice. Only unlinked (or dangling) services remain; a professional with none uses
                // "Create it from the template".
                const selectableCandidates = fixState.candidates.filter(
                  (candidate) => !candidate.linkedToLiveTemplate,
                );
                return (
                  <div
                    key={line.templateId}
                    style={{
                      border: '1px solid #e2e8f0',
                      borderRadius: '10px',
                      padding: '16px',
                      marginBottom: '14px',
                      background: '#f8fafc'
                    }}
                  >
                    <div style={{ fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>
                      {line.name}
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => setFixChoice(line.templateId, { mode: 'link' })}
                        style={{
                          padding: '8px 14px',
                          borderRadius: '8px',
                          border: choice.mode === 'link' ? '2px solid #2d6da3' : '1px solid #cbd5e1',
                          background: choice.mode === 'link' ? '#eff6ff' : 'white',
                          color: choice.mode === 'link' ? '#1d4ed8' : '#475569',
                          fontWeight: '600',
                          fontSize: '13px',
                          cursor: 'pointer'
                        }}
                      >
                        Use one of my services
                      </button>
                      <button
                        type="button"
                        onClick={() => setFixChoice(line.templateId, { mode: 'create' })}
                        style={{
                          padding: '8px 14px',
                          borderRadius: '8px',
                          border: choice.mode === 'create' ? '2px solid #2d6da3' : '1px solid #cbd5e1',
                          background: choice.mode === 'create' ? '#eff6ff' : 'white',
                          color: choice.mode === 'create' ? '#1d4ed8' : '#475569',
                          fontWeight: '600',
                          fontSize: '13px',
                          cursor: 'pointer'
                        }}
                      >
                        Create it from the template
                      </button>
                    </div>

                    {choice.mode === 'create' ? (
                      <div style={{ fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>
                        A copy of the shop's template will be added to your services, using its price
                        and duration. You can edit it afterwards.
                      </div>
                    ) : selectableCandidates.length === 0 ? (
                      <div style={{ fontSize: '13px', color: '#92400e', lineHeight: 1.5 }}>
                        You have no unlinked services to choose from. Create a copy from the template
                        instead.
                      </div>
                    ) : (
                      <select
                        value={choice.serviceId || ''}
                        onChange={(e) =>
                          setFixChoice(line.templateId, {
                            mode: 'link',
                            serviceId: e.target.value,
                          })
                        }
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          border: '1px solid #cbd5e1',
                          fontSize: '14px',
                          color: '#1e293b',
                          background: 'white'
                        }}
                      >
                        <option value="">Choose one of your services…</option>
                        {selectableCandidates.map((candidate) => (
                          <option key={candidate._id} value={candidate._id}>
                            {candidate.name}
                            {line.name &&
                            candidate.name &&
                            candidate.name.trim().toLowerCase() ===
                              line.name.trim().toLowerCase()
                              ? ' (same name)'
                              : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                );
              })}

              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <button
                  type="button"
                  onClick={() => setFixState(null)}
                  disabled={fixState.submitting}
                  style={{
                    flex: 1,
                    padding: '14px 24px',
                    background: '#f1f5f9',
                    color: '#475569',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: fixState.submitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitFixPath}
                  disabled={fixState.submitting}
                  style={{
                    flex: 1,
                    padding: '14px 24px',
                    background: fixState.submitting
                      ? '#9ca3af'
                      : 'linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: fixState.submitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  {fixState.submitting ? 'Saving…' : 'Save and continue'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </StaffSidebarLayout>
  );
}

export default PackageSubscriptions;
